import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { PageHero } from '../components/PageHero';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageSrc = (url) => {
  if (!url) return '';
  // Cloudinary URLs (already absolute) - return as-is
  if (url.startsWith('https://res.cloudinary.com/')) return url;
  // Other absolute URLs - return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Legacy GridFS format: /api/media/... → served from backend
  if (url.startsWith('/api/media/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  // Legacy: /uploads/... → redirect to /api/media/
  if (url.startsWith('/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}/api/media/${url.replace('/uploads/', '')}`;
  return url;
};

// Fallback images if no images in database
const defaultImages = [
  {
    id: 'default-1',
    src: 'https://nauticabrusa.ch/wp-content/uploads/Foto-Werft-1-scaled-500x300.jpg',
    category: 'workshop',
    title_it: 'Il nostro cantiere', title_de: 'Unsere Werft', title_fr: 'Notre chantier', title_en: 'Our shipyard'
  },
  {
    id: 'default-2',
    src: 'https://nauticabrusa.ch/wp-content/uploads/invernaggio2-scaled-500x300.jpg',
    category: 'storage',
    title_it: 'Deposito invernale', title_de: 'Winterlager', title_fr: 'Dépôt hivernal', title_en: 'Winter storage'
  },
  {
    id: 'default-3',
    src: 'https://nauticabrusa.ch/wp-content/uploads/marcobrusa25-500x300.jpg',
    category: 'workshop',
    title_it: 'Trasporto yacht', title_de: 'Yachttransport', title_fr: 'Transport de yacht', title_en: 'Yacht transport'
  },
  {
    id: 'default-4',
    src: 'https://nauticabrusa.ch/wp-content/uploads/IMG_8826-500x300.jpg',
    category: 'workshop',
    title_it: 'Manutenzione', title_de: 'Wartung', title_fr: 'Maintenance', title_en: 'Maintenance'
  },
  {
    id: 'default-5',
    src: 'https://nauticabrusa.ch/wp-content/uploads/P8227616-min-400x300.jpg',
    category: 'workshop',
    title_it: 'Vista cantiere', title_de: 'Werftansicht', title_fr: 'Vue du chantier', title_en: 'Shipyard view'
  },
  {
    id: 'default-6',
    src: 'https://nauticabrusa.ch/wp-content/uploads/riva_hp-850x478-400x300.jpg',
    category: 'yacht',
    title_it: 'Yacht di lusso', title_de: 'Luxusyacht', title_fr: 'Yacht de luxe', title_en: 'Luxury yacht'
  },
];

export const GalleryPage = () => {
  const { t, language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteImages, setSiteImages] = useState({ page_heroes: {} });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleryRes, imagesRes] = await Promise.all([
          axios.get(`${API}/gallery`),
          axios.get(`${API}/site-images`)
        ]);
        if (galleryRes.data && galleryRes.data.length > 0) {
          setImages(galleryRes.data);
        } else {
          setImages(defaultImages);
        }
        setSiteImages(imagesRes.data);
      } catch (err) {
        console.error('Error fetching gallery:', err);
        setImages(defaultImages);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const categories = [
    { key: 'all', label: { it: 'Tutti', de: 'Alle', fr: 'Tous', en: 'All' } },
    { key: 'yacht', label: { it: 'Yacht', de: 'Yachten', fr: 'Yachts', en: 'Yachts' } },
    { key: 'workshop', label: { it: 'Cantiere', de: 'Werft', fr: 'Chantier', en: 'Shipyard' } },
    { key: 'storage', label: { it: 'Deposito', de: 'Lager', fr: 'Dépôt', en: 'Storage' } },
    { key: 'lake', label: { it: 'Lago', de: 'See', fr: 'Lac', en: 'Lake' } },
  ];

  const filteredImages = filter === 'all' 
    ? images 
    : images.filter(img => img.category === filter);

  const getTitle = (img) => {
    return img[`title_${language}`] || img.title_it || '';
  };

  // Mondrian-style grid patterns
  const getGridClass = (index, total) => {
    // Create varying sizes for Mondrian effect
    const patterns = [
      'col-span-2 row-span-2', // Large square
      'col-span-1 row-span-1', // Small square
      'col-span-2 row-span-1', // Wide rectangle
      'col-span-1 row-span-2', // Tall rectangle
      'col-span-1 row-span-1', // Small square
      'col-span-1 row-span-1', // Small square
    ];
    return patterns[index % patterns.length];
  };

  // Helper to get background image URL
  const getHeroBg = () => siteImages.page_heroes?.gallery || '';
  const hasHeroBg = !!getHeroBg();

  return (
    <div data-testid="gallery-page" className="pt-20 bg-white min-h-screen">
      {/* Hero with optional background - always dark */}
      <section className={`relative py-16 overflow-hidden ${hasHeroBg ? '' : 'bg-[#0B1221]'}`}>
        {/* Background Image/Video */}
        {hasHeroBg && (
          <>
            <div
              className="absolute inset-0 w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${getHeroBg()})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B1221]/70 via-[#0B1221]/60 to-[#0B1221]/70" />
          </>
        )}

        <div className="relative z-10 max-w-full mx-auto px-2 sm:px-3 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
              {t('gallery.title')}
            </h1>
            <p className="text-lg text-white/80">{t('gallery.subtitle')}</p>
          </motion.div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {categories.map((cat) => (
              <button
                key={cat.key}
                data-testid={`gallery-filter-${cat.key}`}
                onClick={() => setFilter(cat.key)}
                className={`px-6 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  filter === cat.key
                    ? 'bg-white text-[#0B1221]'
                    : 'bg-white/20 text-white hover:bg-white hover:text-[#0B1221]'
                }`}
              >
                {cat.label[language]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Mondrian Gallery Grid - Full Width */}
      <section className="p-[2px] bg-white">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B1221]"></div>
          </div>
        ) : (
          <div 
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 auto-rows-[150px] sm:auto-rows-[200px] lg:auto-rows-[250px] gap-[2px]"
          >
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id || index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                data-testid={`gallery-image-${index}`}
                onClick={() => setSelectedImage(image)}
                className={`group cursor-pointer overflow-hidden bg-[#F5F5F7] ${getGridClass(index, filteredImages.length)}`}
              >
                <div className="relative w-full h-full">
                  <img
                    src={getImageSrc(image.src)}
                    alt={getTitle(image)}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <p className="text-white p-3 sm:p-4 font-medium text-sm sm:text-base">
                      {getTitle(image)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 text-white hover:text-[#FF3B30] transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <motion.img
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            src={getImageSrc(selectedImage.src)}
            alt={getTitle(selectedImage)}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {getTitle(selectedImage) && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-lg bg-black/50 px-4 py-2 rounded-full">
              {getTitle(selectedImage)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
